// Weekly internal reports (brief section 14): compiled from the database by the worker, one per
// week, facts apart from hypotheses. Readable on a phone.
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { Badge, Empty, ErrorMsg, Loading, PageHead, Section, Time, Unknown } from '../components/ui';
import { api, errorText, newIdempotencyKey } from '../lib/api';
import type { ChannelMetric, ReportList, ReportSummary } from '../lib/deepTypes';
import { countryName, fmtDate, jobType, TERMINAL } from '../lib/format';
import { useApi, useSession } from '../lib/session';

const WEEKDAY = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

function weekLabel(r: { period_start: string; period_end: string }) {
  const end = new Date(r.period_end + 'T00:00:00Z');
  end.setUTCDate(end.getUTCDate() - 1);
  return `${r.period_start} – ${end.toISOString().slice(0, 10)}`;
}

export function Reports() {
  const { id } = useParams();
  return id ? <ReportPage id={id} /> : <ReportIndex />;
}

function ReportIndex() {
  const list = useApi<ReportList>('/api/reports');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [job, setJob] = useState<{ id: string; status: string } | null>(null);
  const reload = list.reload;

  useEffect(() => {
    if (!job || TERMINAL.includes(job.status)) return;
    const t = window.setTimeout(async () => {
      try {
        const j = await api<{ id: string; status: string }>(`/api/jobs/${job.id}`);
        setJob(j);
        if (TERMINAL.includes(j.status)) reload();
      } catch (e) {
        setErr(errorText(e));
      }
    }, 1500);
    return () => window.clearTimeout(t);
  }, [job, reload]);

  async function compile() {
    setBusy(true);
    setErr('');
    try {
      const r = await api<{ job_id: string; status: string }>('/api/reports/weekly', { body: {}, idempotencyKey: newIdempotencyKey() });
      setJob({ id: r.job_id, status: r.status });
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  if (list.loading && !list.data) return <Loading what="Loading reports" />;
  if (!list.data) return <ErrorMsg>{list.error}</ErrorMsg>;
  const d = list.data;
  return (
    <div className="page">
      <PageHead title="Reports" meta="Weekly internal report">
        <button type="button" className="btn primary small" disabled={busy || (!!job && !TERMINAL.includes(job.status))} onClick={() => void compile()}>
          Compile last week now
        </button>
      </PageHead>
      <p className="small muted">
        {d.schedule ? (
          <>
            Compiled automatically every {WEEKDAY[d.settings.weekday]} at {d.settings.time} ({d.timezone}) for the seven days before; next run{' '}
            <Time iso={d.schedule.next_run_at} />.
          </>
        ) : (
          'The weekly schedule is off (Settings → Reports).'
        )}{' '}
        Last complete week: {weekLabel({ period_start: d.last_complete_week.start, period_end: d.last_complete_week.end })}.
      </p>
      {job && (
        <p className="small">
          {jobType('compile_weekly_report')}: {job.status} · <Link to={`/jobs/${job.id}`}>job</Link>
        </p>
      )}
      <ErrorMsg>{err}</ErrorMsg>
      {d.items.length === 0 ? (
        <Empty>No report yet. The first one is compiled on the next scheduled run, or now with the button above.</Empty>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Week</th>
                <th>Status</th>
                <th className="num">New studios</th>
                <th className="num">Material changes</th>
                <th className="num">Failed jobs</th>
                <th>Suggestions</th>
                <th>Compiled</th>
              </tr>
            </thead>
            <tbody>
              {d.items.map((r) => (
                <tr key={r.id}>
                  <td>
                    <Link to={`/reports/${r.id}`}>{weekLabel(r)}</Link>
                  </td>
                  <td>
                    <Badge tone={r.status === 'reviewed' ? 'ok' : undefined}>{r.status}</Badge> <span className="muted small">rev {r.revision}</span>
                  </td>
                  <td className="num">{r.summary?.discovered ?? '—'}</td>
                  <td className="num">{r.summary?.changes ?? '—'}</td>
                  <td className="num">{r.summary?.failed_jobs ?? '—'}</td>
                  <td className="small">{r.summary?.suggestions ?? '—'}</td>
                  <td className="small">
                    <Time iso={r.generated_at} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Posting({ ch }: { ch: ChannelMetric }) {
  return (
    <li>
      {ch.channel}: {ch.posts_observed === null ? <Unknown>{ch.label ?? 'not measured'}</Unknown> : ch.label}
    </li>
  );
}

function ReportPage({ id }: { id: string }) {
  const rep = useApi<ReportSummary>(`/api/reports/${id}`);
  const { user } = useSession();
  const [note, setNote] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  async function setStatus(status: string) {
    setBusy(true);
    setErr('');
    try {
      await api(`/api/reports/${id}/review`, { body: { status, note: note || null } });
      rep.reload();
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  if (rep.loading && !rep.data) return <Loading what="Loading the report" />;
  if (!rep.data?.sections) return <ErrorMsg>{rep.error || 'Report not found.'}</ErrorMsg>;
  const r = rep.data;
  const s = r.sections!;
  const sug = s.suggestions;
  return (
    <div className="page narrow report">
      <p className="crumbs small">
        <Link to="/reports">Reports</Link> / week {weekLabel(r)}
      </p>
      <PageHead title={`Week ${weekLabel(r)}`} meta={`Weekly report · ${r.status} · revision ${r.revision}`} />
      <p className="small muted">
        Evidence up to <Time iso={r.evidence_cutoff} /> ({r.timezone}). Facts come from the database; lines marked “Hypothesis” or “Model
        suggestion” are not findings.
      </p>

      <Section title="Suggested actions">
        {sug?.state === 'ready' ? (
          sug.items && sug.items.length > 0 ? (
            <>
              <p className="small muted">
                {sug.label} {sug.model && `(${sug.model.provider} ${sug.model.name ?? ''}, ${sug.model.is_local ? 'local' : 'cloud'})`}
              </p>
              <ol>
                {sug.items.map((a, i) => (
                  <li key={i} className="stack-s">
                    <p>{a.action}</p>
                    <p className="small">How to evaluate: {a.evaluate}</p>
                    <p className="small muted">Based on: {a.observation_ids.join(', ')}</p>
                  </li>
                ))}
              </ol>
            </>
          ) : (
            <Empty>The model proposed no action it could tie to this week’s observations.</Empty>
          )
        ) : sug?.state === 'waiting' ? (
          <p className="small">
            Waiting for a model agent (GPU worker or OpenClaw) · <Link to={`/jobs/${sug.job_id}`}>job</Link>
          </p>
        ) : (
          <p className="msg info small">{sug?.reason ?? 'No suggestions were requested for this report.'}</p>
        )}
        {sug?.dropped_without_evidence ? <p className="small muted">{sug.dropped_without_evidence} suggestion(s) dropped: they cited no observation.</p> : null}
      </Section>

      <Section title={`New studios on the map (${s.discovered.count}${s.discovered.demo_count ? ` + ${s.discovered.demo_count} demo` : ''})`}>
        {s.discovered.items.length === 0 ? (
          <Empty>None this week.</Empty>
        ) : (
          <ul className="plain">
            {s.discovered.items.map((x) => (
              <li key={x.id}>
                <Link to={`/competitors/${x.id}`}>{x.name}</Link> <span className="muted small">· {[x.city, x.country && countryName(x.country)].filter(Boolean).join(', ') || 'location unknown'} · {x.completeness}</span>
                {x.is_demo && <span className="badge demo">DEMO</span>}
              </li>
            ))}
          </ul>
        )}
        <p className="small muted">{s.discovered.note}</p>
      </Section>

      <Section title={`Deep competitors: material changes (${s.deep_changes.count})`}>
        {s.deep_changes.items.length === 0 ? (
          <Empty>No material change detected this week.</Empty>
        ) : (
          <ul className="runs">
            {s.deep_changes.items.map((c) => (
              <li key={c.id} className="run">
                <p>
                  <Link to={`/competitors/${c.studio_id}?tab=changes`}>{c.studio_name}</Link> · {c.categories.join(', ')} ·{' '}
                  <span className="small muted">{fmtDate(c.detected_at)}</span>
                </p>
                {c.added.length > 0 && <p className="small">+ {c.added.join(' · ')}</p>}
                {c.hypothesis && <p className="small hypothesis">{c.hypothesis}</p>}
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Observed posting activity and coverage">
        {s.posting.studios.length === 0 ? (
          <Empty>No Deep studio.</Empty>
        ) : (
          <ul className="plain">
            {s.posting.studios.map((st) => (
              <li key={st.studio_id}>
                <Link to={`/competitors/${st.studio_id}?tab=social`}>{st.name}</Link>
                {st.channels.length === 0 ? (
                  <span className="muted small"> · website only (no social channel watched)</span>
                ) : (
                  <ul className="plain small">
                    {st.channels.map((ch) => (
                      <Posting key={ch.channel} ch={ch} />
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        )}
        <p className="small muted">{s.posting.note}</p>
      </Section>

      <Section title="New reviewed pricing evidence">
        <p>
          {s.pricing.approved_quotations} quotation(s) approved from {s.pricing.distinct_suppliers} supplier(s).{' '}
          <Link to="/pricing">Pricing</Link>
        </p>
        <p className="small muted">{s.pricing.note}</p>
      </Section>

      <Section title="Content ready">
        <p className="small">
          Waiting for review: {s.content.needs_review.length ? s.content.needs_review.map((i) => i.title).join(', ') : 'none'}
          <br />
          Approved, not exported: {s.content.approved_not_exported.length ? s.content.approved_not_exported.map((i) => i.title).join(', ') : 'none'}
        </p>
      </Section>

      {s.prospects && (
        <Section title="Prospects (recorded by hand)">
          <p className="small">
            {Object.entries(s.prospects.interactions).map(([k, v]) => `${v} ${k}`).join(' · ') || 'No interaction recorded this week.'} · follow-ups due:{' '}
            {s.prospects.follow_ups_due}
          </p>
          <p className="small muted">{s.prospects.note}</p>
        </Section>
      )}

      <Section title="Failed or incomplete jobs">
        {s.jobs.failed.length + s.jobs.partial.length === 0 ? (
          <Empty>None this week.</Empty>
        ) : (
          <ul className="plain small">
            {[...s.jobs.failed, ...s.jobs.partial].map((j) => (
              <li key={j.id}>
                <Link to={`/jobs/${j.id}`}>{jobType(j.type)}</Link> · {j.error || 'no error text'}
              </li>
            ))}
          </ul>
        )}
        {s.jobs.needs_attention > 0 && <p className="small">{s.jobs.needs_attention} job(s) need attention.</p>}
      </Section>

      <Section title="Limits of this report">
        <ul className="plain small">
          {s.limitations.map((l) => (
            <li key={l}>{l}</li>
          ))}
        </ul>
      </Section>

      {user?.role === 'owner' && (
        <Section title="Review">
          <label className="field">
            <span>Note (optional)</span>
            <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
          </label>
          <div className="row gap">
            {r.status === 'draft' ? (
              <button type="button" className="btn primary small" disabled={busy} onClick={() => void setStatus('reviewed')}>
                Mark reviewed (freezes it)
              </button>
            ) : (
              <button type="button" className="btn small" disabled={busy} onClick={() => void setStatus('draft')}>
                Reopen as draft
              </button>
            )}
          </div>
          {r.review_note && <p className="small muted">Review note: {r.review_note}</p>}
          <ErrorMsg>{err}</ErrorMsg>
        </Section>
      )}
    </div>
  );
}
